import { IMG_ROOT } from "../../lib/constants";

export type JobStatus = "QUEUED" | "TRANSFERRING" | "QUOTA_EXCEEDED" | "FAILED" | "COMPLETED";

export interface TransferJobView {
  id: string;
  artworkId: number;
  title: string;
  lowResPath: string;
  status: JobStatus;
  error: string;
  /** Product templates this job will create, by name. */
  products: string[];
  /** Stores it will create them in. */
  stores: string[];
  /** Filled in once the job finishes: what actually reached each store. */
  detail?: { store: string; productId: number; images: number }[];
}

export interface TransferHistoryEntry {
  id: string;
  artworkId: number;
  title: string;
  lowResPath: string;
  status: string;
  platformId: string;
  by: string;
  at: string;
  error: string;
}

export interface TransfersPanelProps {
  jobs: TransferJobView[];
  /** Display name of the member currently running a transfer, when it is not us. */
  lockedBy: string | null;
  /** Seconds until the Shopify daily limit lifts, or null when not paused. */
  pausedSeconds: number | null;
  running: boolean;
  /** Set when the run stopped on a template error the user has to fix. */
  failure: string | null;
  onAbort: () => void;
  onRetry: () => void;
  /** History view: null = not loaded/closed, [] = loaded but empty. */
  history: TransferHistoryEntry[] | null;
  historyLoading: boolean;
  onShowHistory: () => void;
  onHideHistory: () => void;
}

/** `88:12:34` — the countdown has to read clearly at a glance while the run is parked. */
function formatCountdown(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function JobStatusLine({ job }: { job: TransferJobView }) {
  switch (job.status) {
    case "QUEUED":
      return (
        <span class="transfers-status transfers-status-queued">
          <i class="far fa-clock"></i> In queue
        </span>
      );
    case "TRANSFERRING":
      return (
        <span class="transfers-status transfers-status-active">
          <i class="fas fa-exchange-alt fa-spin"></i> Transferring…
        </span>
      );
    case "QUOTA_EXCEEDED":
      return (
        <span class="transfers-status transfers-status-active">
          <i class="fas fa-info-circle"></i> Waiting for Shopify limit
        </span>
      );
    case "FAILED":
      return (
        <span class="transfers-status transfers-status-failed">
          <i class="fas fa-exclamation-triangle"></i> Failed
        </span>
      );
    case "COMPLETED":
      return (
        <span class="transfers-status transfers-status-done">
          <i class="fas fa-check-circle"></i> Done
        </span>
      );
  }
}

/** One line of "what is this actually doing", live while transferring and after it finishes. */
function JobDetail({ job }: { job: TransferJobView }) {
  if (job.status === "COMPLETED" && job.detail && job.detail.length > 0) {
    return (
      <div class="transfers-row-detail">
        {job.detail.map((d) => (
          <div key={`${d.store}-${d.productId}`}>
            {d.store}: product {d.productId} · {d.images} image{d.images === 1 ? "" : "s"}
          </div>
        ))}
      </div>
    );
  }
  // Before/while it runs, say what it is going to do — the server does one job per call, so
  // there is no finer-grained live signal to show without splitting the request up.
  if (job.status === "TRANSFERRING" || job.status === "QUEUED") {
    const products = job.products.length;
    const stores = job.stores.length;
    if (products === 0 && stores === 0) return null;
    return (
      <div class="transfers-row-detail">
        {products} template{products === 1 ? "" : "s"} → {job.stores.join(", ")}
      </div>
    );
  }
  return null;
}

function HistoryList({ entries }: { entries: TransferHistoryEntry[] }) {
  if (entries.length === 0) {
    return <div class="transfers-empty-block">No previous transfers.</div>;
  }
  return (
    <>
      {entries.map((e) => (
        <div class="transfers-row" key={e.id}>
          {e.lowResPath && <img class="transfers-thumb" src={`${IMG_ROOT}${e.lowResPath}`} />}
          <div class="transfers-row-body">
            <div class="transfers-row-title">{e.title || `Artwork ${e.artworkId}`}</div>
            {e.status === "COMPLETED" ? (
              <span class="transfers-status transfers-status-done">
                <i class="fas fa-check-circle"></i> Transferred
                {e.platformId ? ` · product ${e.platformId}` : ""}
              </span>
            ) : (
              <span class="transfers-status transfers-status-failed">
                <i class="fas fa-exclamation-triangle"></i> Failed
              </span>
            )}
            <div class="transfers-row-detail">
              {e.at ? new Date(e.at).toLocaleString() : ""}
              {e.by ? ` · ${e.by}` : ""}
            </div>
            {e.status === "FAILED" && e.error && <div class="transfers-row-error">{e.error}</div>}
          </div>
        </div>
      ))}
    </>
  );
}

export function TransfersPanel({
  jobs,
  lockedBy,
  pausedSeconds,
  running,
  failure,
  onAbort,
  onRetry,
  history,
  historyLoading,
  onShowHistory,
  onHideHistory,
}: TransfersPanelProps) {
  // Another member of this reseller holds the lock. We show who, and nothing else — the
  // Shopify store is shared, so a second client must not start.
  if (lockedBy) {
    return (
      <div class="transfers-notice">
        <div class="transfers-notice-title">
          <i class="fas fa-lock"></i> Transfers in use
        </div>
        <div>
          A transfer is already running. In use by <b>{lockedBy}</b>.
        </div>
        <div class="transfers-notice-sub">
          Only one transfer can run at a time for your account.
        </div>
      </div>
    );
  }

  if (history !== null) {
    return (
      <>
        <div class="transfers-history-head">
          <span class="transfers-history-title">Transfer history</span>
          <span class="button small transfers-light-btn" onClick={onHideHistory}>
            Back
          </span>
        </div>
        {historyLoading ? (
          <div class="transfers-empty-block">Loading…</div>
        ) : (
          <HistoryList entries={history} />
        )}
      </>
    );
  }

  const done = jobs.filter((j) => j.status === "COMPLETED").length;
  const total = jobs.length;

  // Nothing queued: centre the message and offer the log, rather than leaving an empty box.
  if (total === 0 && !failure && pausedSeconds === null) {
    return (
      <div class="transfers-empty-block">
        <div class="transfers-empty-text">No transfers in queue.</div>
        <span class="button small transfers-dock-btn transfers-light-btn" onClick={onShowHistory}>
          {historyLoading ? "Loading…" : "Show history"}
        </span>
      </div>
    );
  }

  return (
    <>
      {pausedSeconds !== null && (
        <div class="transfers-notice">
          <div class="transfers-notice-title">
            <i class="fas fa-hourglass-half"></i> Shopify daily limit reached
          </div>
          <div>
            Transfers resume in <b>{formatCountdown(pausedSeconds)}</b>
          </div>
          <div class="transfers-notice-sub">
            We&rsquo;ll continue automatically if you keep this page open — otherwise the rest
            resumes next time you visit.
          </div>
        </div>
      )}

      {failure && (
        <div class="transfers-notice transfers-notice-error">
          <div class="transfers-notice-title">
            <i class="fas fa-exclamation-triangle"></i> Transfer stopped
          </div>
          <div>{failure}</div>
          <div class="transfers-notice-sub">
            Fix the product template, then retry.{" "}
            <span class="transfers-retry" onClick={onRetry}>
              Retry now
            </span>
          </div>
        </div>
      )}

      {total > 0 && (
        <div class="transfers-progress">
          {done} of {total} transferred
        </div>
      )}

      {running && (
        <div class="transfers-warning">
          <i class="fas fa-exclamation-triangle transfers-warning-icon"></i>
          <span>Transfer in progress. Do not close this window.</span>
        </div>
      )}

      {jobs.map((job) => (
        <div class="transfers-row" key={job.id}>
          {job.lowResPath && <img class="transfers-thumb" src={`${IMG_ROOT}${job.lowResPath}`} />}
          <div class="transfers-row-body">
            <div class="transfers-row-title">{job.title || `Artwork ${job.artworkId}`}</div>
            <JobStatusLine job={job} />
            <JobDetail job={job} />
            {job.status === "FAILED" && job.error && (
              <div class="transfers-row-error">{job.error}</div>
            )}
          </div>
        </div>
      ))}

      <div class="transfers-actions">
        {running && (
          <span class="button small transfers-dock-btn transfers-abort" onClick={onAbort}>
            Abort all transfers
          </span>
        )}
        {!running && (
          <span class="button small transfers-dock-btn transfers-light-btn" onClick={onShowHistory}>
            {historyLoading ? "Loading…" : "Show history"}
          </span>
        )}
      </div>
    </>
  );
}
